import AlertDialog from '@src/components/dialog/AlertDialog';
import type { ComponentProps, ReactNode } from 'react';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';

import { ALERT_ACTIONS, type AlertAction } from './constants';

type AlertResult = { action: AlertAction };
type AlertDialogProps = ComponentProps<typeof AlertDialog>;
type AlertState = AlertDialogProps & { open: boolean };

type AlertContextValue = {
  open: (props: AlertDialogProps) => Promise<AlertResult>;
  close: (payload?: Partial<AlertResult>) => void;
  ALERT_ACTIONS: typeof ALERT_ACTIONS;
};

const AlertContext = createContext<AlertContextValue | null>(null);

type AlertProviderProps = {
  children: ReactNode;
};

function AlertProvider({ children }: AlertProviderProps) {
  const resolvePromise = useRef<((value: AlertResult) => void) | null>(null);

  const [state, setState] = useState<AlertState>({ open: false } as AlertState);

  const open = useCallback((props: AlertDialogProps) => {
    setState({ ...props, open: true });
    return new Promise<AlertResult>((resolve) => {
      resolvePromise.current = resolve;
    });
  }, []);

  const close = useCallback(
    ({ action = ALERT_ACTIONS.CLOSE }: Partial<AlertResult> = {}) => {
      setState((prevState: AlertState) => ({ ...prevState, open: false }));
      resolvePromise.current?.({ action });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    close({ action: ALERT_ACTIONS.CANCEL });
  }, [close]);

  const handleSubmitModal = useCallback(() => {
    close({ action: ALERT_ACTIONS.CONFIRM });
  }, [close]);

  const contextValue = useMemo(
    () => ({ open, close, ALERT_ACTIONS }),
    [open, close],
  );

  return (
    <AlertContext.Provider value={contextValue}>
      {children}

      <AlertDialog
        {...state}
        disableEnforceFocus
        onClose={handleCloseModal}
        onConfirm={handleSubmitModal}
      />
    </AlertContext.Provider>
  );
}

export { AlertContext, AlertProvider };
